import React, { useState } from "react";
import styled from "@emotion/styled";
import { ApplicationRevenue, BillingConfig, ContractType, OrganizationRevenue } from "../../../models/revenue/BillingConfig";
import { matchesRevenueSearch } from "../../../utils/revenueFilters";
import IconTooltip from "../../../components/general/IconTooltip";
import ApplicationLayoutModel from "../../../models/applications/api/ApplicationLayoutModel";
import { compareRevenueApplications, getRevenueGroupName } from "../../../utils/revenueNaming";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
} from "../../../constants/colors";

interface RevenueTableProps {
    orgRevenue: OrganizationRevenue;
    ignoredAppIds: string[];
    // Names of applications marked Not paying, listed so they can be moved back.
    notPayingApps?: { id: string; name: string }[];
    onToggleIgnore: (appId: string) => void;
    // Opens the full rate editor, for rates beyond the ones edited in the row.
    onEditOverride: (appId: string) => void;
    hasOverride: (appId: string) => boolean;
    // The application's current rates (its own, or the organization's).
    getRates?: (appId: string) => BillingConfig;
    // Saves rate changes; null returns the application to the organization's rates.
    onSaveRates?: (appId: string, changes: Partial<BillingConfig> | null) => Promise<boolean>;
    isSavingRates?: boolean;
    saveRatesError?: string | null;
    organizationName: string;
    applicationLayout: ApplicationLayoutModel | null | undefined;
    contractType?: ContractType;
    // Global search from the page; when set, only applications whose names match are shown.
    applicationSearch?: string;
}

const infoIcon = require("../../../content/images/info_icon.svg");

type SortColumn = "organization" | "group" | "application";

interface RateInput {
    field: keyof BillingConfig;
    label: string;
}

// The rates Robert adjusts, edited in place in the cell they drive: the monthly fee in Flat Fee,
// and the per-unit rate in the metric each contract type bills on.
const getRateInputs = (columnKey: string, contractType?: ContractType): RateInput[] => {
    if (columnKey === "flatFee") return [{ field: "flatMonthlyFee", label: "$/mo" }];
    if (columnKey === "calls" && contractType === "AI Answering") return [{ field: "callRate", label: "$/call" }];
    if (columnKey === "orders" && contractType === "AI Ordering") return [{ field: "orderPlacedRate", label: "$/order" }];
    if (columnKey === "inbound" && contractType === "Medical") return [{ field: "aiMinuteRate", label: "$/min" }];
    if (columnKey === "outbound" && contractType === "Medical") return [{ field: "outboundAiMinuteRate", label: "$/min" }];
    return [];
};

const EDITABLE_COLUMN_KEYS = ["flatFee", "calls", "orders", "inbound", "outbound"];

interface MetricColumn {
    key: string;
    label: string;
    decimals: number;
    bold?: boolean;
    // Explains columns whose purpose isn't obvious from the label.
    help?: string;
    count: (app: ApplicationRevenue) => number;
    revenue: (app: ApplicationRevenue) => number;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const formatNumber = (value: number, decimals: number = 1): string => {
    return value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
};

const RevenueTable: React.FC<RevenueTableProps> = ({
    orgRevenue,
    ignoredAppIds,
    notPayingApps = [],
    onToggleIgnore,
    onEditOverride,
    hasOverride,
    organizationName,
    applicationLayout,
    contractType,
    applicationSearch = "",
    getRates,
    onSaveRates,
    isSavingRates = false,
    saveRatesError,
}) => {
    const [sortColumn, setSortColumn] = useState<SortColumn>("group");
    const [editingAppId, setEditingAppId] = useState<string | null>(null);
    const [rateDraft, setRateDraft] = useState<Partial<Record<keyof BillingConfig, string>>>({});
    const [rateError, setRateError] = useState("");
    const editableFields = EDITABLE_COLUMN_KEYS.reduce<RateInput[]>(
        (fields, key) => fields.concat(getRateInputs(key, contractType)), []);

    const startEditing = (appId: string) => {
        if (!getRates || !onSaveRates) {
            onEditOverride(appId);
            return;
        }
        const rates = getRates(appId);
        setRateDraft(editableFields.reduce((draft, { field }) => ({ ...draft, [field]: String(rates[field]) }), {}));
        setRateError("");
        setEditingAppId(appId);
    };

    const saveRates = async (appId: string, reset: boolean = false) => {
        if (!onSaveRates) return;
        setRateError("");
        if (reset) {
            if (await onSaveRates(appId, null)) setEditingAppId(null);
            return;
        }
        const changes: Partial<BillingConfig> = {};
        for (const { field } of editableFields) {
            const value = Number((rateDraft[field] || "").trim());
            if ((rateDraft[field] || "").trim() === "" || !Number.isFinite(value) || value < 0) {
                setRateError("Rates must be numbers of 0 or more.");
                return;
            }
            (changes as Record<string, number>)[field] = value;
        }
        // On failure the container's save error is shown below the actions.
        if (await onSaveRates(appId, changes)) setEditingAppId(null);
    };

    const renderRateInputs = (inputs: RateInput[]) => inputs.map(({ field, label }) => (
        <RateField key={field}>
            <RateInputBox
                type="number"
                min="0"
                step="0.01"
                aria-label={`${label} rate`}
                value={rateDraft[field] ?? ""}
                onChange={e => setRateDraft({ ...rateDraft, [field]: e.target.value })}
            />
            <span>{label}</span>
        </RateField>
    ));
    const [sortDescending, setSortDescending] = useState(false);
    const [organizationFilter, setOrganizationFilter] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    const [applicationFilter, setApplicationFilter] = useState("");

    const getGroupName = (app: ApplicationRevenue) => getRevenueGroupName(applicationLayout, app.applicationId);
    const getAppName = (app: ApplicationRevenue) => app.applicationName || app.applicationId;
    const compareDefault = (a: ApplicationRevenue, b: ApplicationRevenue) => compareRevenueApplications(
        applicationLayout,
        { id: a.applicationId, name: getAppName(a) },
        { id: b.applicationId, name: getAppName(b) }
    );
    const applications = orgRevenue.applications.filter(app =>
        matchesRevenueSearch(getAppName(app), applicationSearch) &&
        organizationName.toLowerCase().includes(organizationFilter.trim().toLowerCase()) &&
        (!groupFilter || (groupFilter === "ungrouped"
            ? !getGroupName(app)
            : applicationLayout?.groups.some(group => group.id === groupFilter && group.appIds.includes(app.applicationId)))) &&
        (getAppName(app).toLowerCase().includes(applicationFilter.trim().toLowerCase()) ||
            app.applicationId.toLowerCase().includes(applicationFilter.trim().toLowerCase()))
    ).sort((a, b) => {
        if (sortColumn === "group") {
            if (!!getGroupName(a) !== !!getGroupName(b)) return getGroupName(a) ? -1 : 1;
            return (sortDescending ? -1 : 1) * getGroupName(a).toLowerCase().localeCompare(getGroupName(b).toLowerCase()) ||
                getAppName(a).toLowerCase().localeCompare(getAppName(b).toLowerCase()) || a.applicationId.localeCompare(b.applicationId);
        }
        if (sortColumn === "application") {
            return (sortDescending ? -1 : 1) * getAppName(a).toLowerCase().localeCompare(getAppName(b).toLowerCase()) || compareDefault(a, b);
        }
        // Organization is shared by all rows in this organization's table.
        return compareDefault(a, b);
    });
    const hasFilters = !!(organizationFilter || groupFilter || applicationFilter);
    const totalRevenue = applications.reduce((sum, app) => sum + app.totalRevenue, 0);
    const sortHeader = (column: SortColumn, label: string) => (
        <Th aria-sort={sortColumn === column ? (sortDescending ? "descending" : "ascending") : "none"}>
            <SortButton onClick={() => {
                setSortColumn(column);
                setSortDescending(sortColumn === column ? !sortDescending : false);
            }}>
                {label}{sortColumn === column ? (sortDescending ? " ▼" : " ▲") : ""}
            </SortButton>
        </Th>
    );

    // Calls only matter for AI Answering billing, so they're hidden for Ordering and Retail Health,
    // unless calls actually add revenue there: anything that feeds a total stays visible.
    const showCalls = (contractType !== "AI Ordering" && contractType !== "Medical")
        || orgRevenue.applications.some(app => app.breakdown.calls.revenue > 0);
    const metricColumns: MetricColumn[] = [
        { key: "orders", label: "Orders", decimals: 0, bold: true,
            count: a => a.breakdown.ordersPlaced.count, revenue: a => a.breakdown.ordersPlaced.revenue },
        { key: "inbound", label: "Inbound Min", decimals: 1,
            count: a => a.breakdown.aiMinutes.count, revenue: a => a.breakdown.aiMinutes.revenue },
        { key: "outbound", label: "Outbound Min", decimals: 1,
            count: a => a.breakdown.outboundAiMinutes.count, revenue: a => a.breakdown.outboundAiMinutes.revenue },
        { key: "minutes", label: "Inbound + Outbound Min", decimals: 1,
            count: a => a.breakdown.aiMinutes.count + a.breakdown.outboundAiMinutes.count,
            revenue: a => a.breakdown.aiMinutes.revenue + a.breakdown.outboundAiMinutes.revenue },
        ...(showCalls ? [{ key: "calls", label: "Calls", decimals: 0,
            count: (a: ApplicationRevenue) => a.breakdown.calls.count,
            revenue: (a: ApplicationRevenue) => a.breakdown.calls.revenue }] : []),
        { key: "postTransfer", label: "PT Min", decimals: 1,
            help: "Post-transfer minutes: time on a call after the AI transferred it to staff. Adds to revenue only when a Post-Transfer Minute Rate is set.",
            count: a => a.breakdown.postTransferMinutes.count, revenue: a => a.breakdown.postTransferMinutes.revenue },
        { key: "contained", label: "Contained", decimals: 0,
            help: "Calls the AI handled start to finish without transferring. Adds to revenue only when a Contained Call Rate is set.",
            count: a => a.breakdown.containedCalls.count, revenue: a => a.breakdown.containedCalls.revenue },
        { key: "nonContainable", label: "Non-Containable", decimals: 0,
            help: "Calls the AI could not have handled on its own (marked not containable). Adds to revenue only when a Non-Containable Call Rate is set.",
            count: a => a.breakdown.nonContainableCalls.count, revenue: a => a.breakdown.nonContainableCalls.revenue },
    ];
    // Renders one metric for a single row, or summed for the totals row.
    const renderMetricCell = (column: MetricColumn, apps: ApplicationRevenue[], isTotal: boolean) => {
        const count = formatNumber(apps.reduce((sum, app) => sum + column.count(app), 0), column.decimals);
        const revenue = formatCurrency(apps.reduce((sum, app) => sum + column.revenue(app), 0));
        const isEditing = !isTotal && apps[0]?.applicationId === editingAppId;
        const rateInputs = isEditing ? renderRateInputs(getRateInputs(column.key, contractType)) : null;
        const content = isTotal || column.bold
            ? <MetricCell><MetricCountBold>{count}</MetricCountBold><MetricRevenueBold>{revenue}</MetricRevenueBold>{rateInputs}</MetricCell>
            : <MetricCell><MetricCount>{count}</MetricCount><MetricRevenue>{revenue}</MetricRevenue>{rateInputs}</MetricCell>;
        return column.bold
            ? <TdOrders key={column.key} align="right">{content}</TdOrders>
            : <Td key={column.key} align="right">{content}</Td>;
    };

    const notPayingList = notPayingApps.length > 0 && (
        <NotPayingSection>
            <NotPayingTitle>Not paying (left out of totals and exports)</NotPayingTitle>
            {notPayingApps.map(app => (
                <NotPayingItem key={app.id}>
                    <span>{app.name}</span>
                    <SmallButton onClick={() => onToggleIgnore(app.id)} title="Bill this application again">
                        Include
                    </SmallButton>
                </NotPayingItem>
            ))}
        </NotPayingSection>
    );

    if (orgRevenue.applications.length === 0) {
        return (
            <>
                <EmptyState>No billable applications found for this month.</EmptyState>
                {notPayingList}
            </>
        );
    }

    return (
        <>
        <TableControls>
            <span>{applications.length} of {orgRevenue.applications.length} applications</span>
            {hasFilters && <SmallButton onClick={() => {
                setOrganizationFilter("");
                setGroupFilter("");
                setApplicationFilter("");
            }}>Clear filters</SmallButton>}
        </TableControls>
        <TableWrapper>
            <Table>
                <thead>
                    <tr>
                        <Th>Organization</Th>
                        {sortHeader("group", "Group")}
                        {sortHeader("application", "Application")}
                        {metricColumns.map(column => column.bold
                            ? <ThOrders key={column.key} align="right">{column.label}</ThOrders>
                            : <Th key={column.key} align="right">
                                {column.help
                                    ? <HeaderWithHelp>{column.label}<IconTooltip text={column.help} icon={infoIcon} place="top" /></HeaderWithHelp>
                                    : column.label}
                            </Th>)}
                        <Th align="right">Flat Fee</Th>
                        <Th align="right">Total Revenue</Th>
                        <Th align="center">Actions</Th>
                    </tr>
                    <tr>
                        <Th><FilterInput aria-label="Filter organization" placeholder="Filter organization" value={organizationFilter} onChange={e => setOrganizationFilter(e.target.value)} /></Th>
                        <Th>
                            <FilterSelect aria-label="Filter group" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
                                <option value="">All groups</option>
                                {(applicationLayout?.groups || []).filter(group => group.name && group.name !== "Other").slice()
                                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                    .map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                                <option value="ungrouped">Ungrouped</option>
                            </FilterSelect>
                        </Th>
                        <Th><FilterInput aria-label="Filter application" placeholder="App name or ID" value={applicationFilter} onChange={e => setApplicationFilter(e.target.value)} /></Th>
                        <Th colSpan={metricColumns.length + 3} />
                    </tr>
                </thead>
                <tbody>
                    {applications.length === 0 && <tr><Td colSpan={metricColumns.length + 6}>No applications match these filters.</Td></tr>}
                    {applications.map((app) => {
                        const isIgnored = ignoredAppIds.includes(app.applicationId);
                        const isOverridden = hasOverride(app.applicationId);
                        const isEditing = app.applicationId === editingAppId;

                        return (
                            <Tr key={app.applicationId} isIgnored={isIgnored}>
                                <TdName>{organizationName}</TdName>
                                <TdName>{getGroupName(app)}</TdName>
                                <TdName>
                                    <AppNameCell>
                                        <AppName>{getAppName(app)}</AppName>
                                        {isOverridden && <OverrideBadge>Custom rates</OverrideBadge>}
                                    </AppNameCell>
                                </TdName>
                                {metricColumns.map(column => renderMetricCell(column, [app], false))}
                                <Td align="right">
                                    <MetricCell>
                                        {formatCurrency(app.breakdown.flatFee)}
                                        {isEditing && renderRateInputs(getRateInputs("flatFee", contractType))}
                                    </MetricCell>
                                </Td>
                                <TdTotal align="right">{formatCurrency(app.totalRevenue)}</TdTotal>
                                <Td align="center">
                                    {isEditing ? (
                                        <EditActions>
                                            <ActionGroup>
                                                <SaveButton disabled={isSavingRates} onClick={() => saveRates(app.applicationId)}>
                                                    {isSavingRates ? "Saving..." : "Save"}
                                                </SaveButton>
                                                <SmallButton onClick={() => setEditingAppId(null)}>Cancel</SmallButton>
                                            </ActionGroup>
                                            <LinkButton onClick={() => onEditOverride(app.applicationId)}>All rates</LinkButton>
                                            {isOverridden && (
                                                <LinkButton
                                                    disabled={isSavingRates}
                                                    onClick={() => saveRates(app.applicationId, true)}
                                                    title="Discard this application's rates and use the organization's"
                                                >
                                                    Use organization rates
                                                </LinkButton>
                                            )}
                                            {(rateError || saveRatesError) && <RateError>{rateError || saveRatesError}</RateError>}
                                        </EditActions>
                                    ) : (
                                        <ActionGroup>
                                            <SmallButton
                                                onClick={() => startEditing(app.applicationId)}
                                                title="Edit this application's monthly fee and rate in the row"
                                            >
                                                Edit
                                            </SmallButton>
                                            <IgnoreButton
                                                isIgnored={isIgnored}
                                                onClick={() => onToggleIgnore(app.applicationId)}
                                                title="Stop billing this application: it moves to Not paying below and is left out of totals and exports"
                                            >
                                                Exclude
                                            </IgnoreButton>
                                        </ActionGroup>
                                    )}
                                </Td>
                            </Tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <TotalRow>
                        <Td colSpan={3}><strong>{hasFilters ? "Filtered Total" : "Organization Total"}</strong></Td>
                        {metricColumns.map(column => renderMetricCell(column, applications, true))}
                        <Td align="right">
                            <MetricCell>
                                <MetricRevenueBold>
                                    {formatCurrency(applications.reduce((s, a) => s + a.breakdown.flatFee, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <TdTotal align="right">
                            <strong>{formatCurrency(totalRevenue)}</strong>
                        </TdTotal>
                        <Td />
                    </TotalRow>
                </tfoot>
            </Table>
        </TableWrapper>
        {notPayingList}
        </>
    );
};

const TableControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    color: ${color_text_light};
    font-size: 12px;
`;

const SortButton = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-transform: inherit;
    cursor: pointer;
`;

const FilterInput = styled.input`
    box-sizing: border-box;
    width: 100%;
    min-width: 140px;
    padding: 6px;
    border: 1px solid ${color_shades_dark};
    border-radius: 3px;
    font-size: 12px;
`;

const FilterSelect = styled.select`
    box-sizing: border-box;
    width: 100%;
    min-width: 110px;
    padding: 6px;
    border: 1px solid ${color_shades_dark};
    border-radius: 3px;
    font-size: 12px;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead tr th:nth-child(even),
    tbody tr td:nth-child(even),
    tfoot tr td:nth-child(even) {
        background-color: rgba(0, 0, 0, 0.025);
    }
`;

const Th = styled.th<{ align?: string }>`
    padding: 10px 8px;
    text-align: ${(p) => p.align || "left"};
    font-size: 11px;
    font-weight: 600;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: ${color_shades_lightest};
    border-bottom: 2px solid ${color_shades_dark};
    white-space: nowrap;
`;

const Tr = styled.tr<{ isIgnored?: boolean }>`
    opacity: ${(p) => (p.isIgnored ? 0.4 : 1)};
    text-decoration: ${(p) => (p.isIgnored ? "line-through" : "none")};

    &:hover {
        background: ${color_shades_lightest};
    }

    &:not(:last-child) td {
        border-bottom: 1px solid ${color_shades_light};
    }
`;

const Td = styled.td<{ align?: string }>`
    padding: 8px;
    text-align: ${(p) => p.align || "left"};
    color: ${color_text_default};
    white-space: nowrap;
`;

const TdName = styled.td`
    padding: 8px;
    text-align: left;
    color: ${color_text_default};
`;

const TdTotal = styled(Td)`
    font-weight: 600;
    color: ${color_colors_growth};
`;

const TotalRow = styled.tr`
    background: ${color_shades_lightest};

    td {
        border-top: 2px solid ${color_shades_dark};
        padding: 10px 8px;
    }
`;

const AppNameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AppName = styled.span`
    font-weight: 500;
`;

const OverrideBadge = styled.span`
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 3px;
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    font-weight: 500;
`;

const MetricCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
`;

const MetricCount = styled.span`
    font-size: 12px;
    color: ${color_text_light};
`;

const MetricRevenue = styled.span`
    font-size: 13px;
    font-weight: 500;
`;

const ActionGroup = styled.div`
    display: flex;
    gap: 4px;
    justify-content: center;
`;

const SmallButton = styled.button`
    padding: 3px 8px;
    font-size: 11px;
    border: 1px solid ${color_colors_ocean};
    color: ${color_colors_ocean};
    background: transparent;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${color_colors_ocean};
        color: ${color_defaults_white};
    }
`;

const IgnoreButton = styled.button<{ isIgnored?: boolean }>`
    padding: 3px 8px;
    font-size: 11px;
    border: 1px solid ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
    color: ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
    background: transparent;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
        color: ${color_defaults_white};
    }
`;

const HeaderWithHelp = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const RateField = styled.label`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
    font-size: 11px;
    color: ${color_text_light};
`;

const RateInputBox = styled.input`
    width: 72px;
    padding: 3px 6px;
    border: 1px solid ${color_colors_ocean};
    border-radius: 3px;
    font-size: 12px;
    text-align: right;
`;

const EditActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const SaveButton = styled.button`
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 600;
    border: none;
    border-radius: 3px;
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const LinkButton = styled.button`
    padding: 0;
    border: none;
    background: none;
    color: ${color_colors_ocean};
    font-size: 11px;
    cursor: pointer;
    text-decoration: underline;
    white-space: nowrap;
`;

const RateError = styled.span`
    max-width: 140px;
    font-size: 11px;
    color: ${color_colors_decline};
    white-space: normal;
`;

const NotPayingSection = styled.div`
    margin-top: 12px;
    padding: 8px 12px;
    border: 1px dashed ${color_shades_light};
    border-radius: 4px;
`;

const NotPayingTitle = styled.div`
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 600;
    color: ${color_text_light};
`;

const NotPayingItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
    color: ${color_text_light};
`;

const EmptyState = styled.div`
    padding: 24px;
    text-align: center;
    color: ${color_text_light};
    font-style: italic;
`;

const ThOrders = styled(Th)`
    color: ${color_text_default};
    font-size: 12px;
`;

const TdOrders = styled(Td)`
    font-weight: 600;
`;

const MetricCountBold = styled(MetricCount)`
    font-weight: 600;
    color: ${color_text_default};
`;

const MetricRevenueBold = styled(MetricRevenue)`
    font-weight: 700;
`;

export default React.memo(RevenueTable);
